import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Slider, Button, Grid } from "@mui/material";
import { useFirebase } from "contexts/Firebase";
import { useAuth } from "contexts/User";
import { doc, getDoc, setDoc } from "firebase/firestore";

type PlanTypePreferences = {
    hmo: number;
    ppo: number;
    shortterm: number;
};

interface PlanTypePreferencesProps {
    onPreferencesSaved?: () => void;
    onPreferencesChanged?: (hasUnsavedChanges: boolean) => void;
}

const PlanTypePref: React.FC<PlanTypePreferencesProps> = ({ 
    onPreferencesSaved,
    onPreferencesChanged 
}) => {
    const { firestore } = useFirebase();
    const { user } = useAuth();
    const [planTypePrefs, setPlanTypePrefs] = useState<PlanTypePreferences>({
        hmo: 0.5,
        ppo: 0.5,
        shortterm: 0.5,
    });
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        const fetchPreferences = async () => {
            if (user?.uid) {
                const docRef = doc(firestore, `users/${user.uid}/answers/preferences`);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    if (data.planType) {
                        setPlanTypePrefs(data.planType);
                    }
                }
            }
        };
        fetchPreferences();
    }, [firestore, user?.uid]);

    const updatePlanTypePreferences = useCallback((key: keyof PlanTypePreferences, value: number) => {
        setPlanTypePrefs(prev => ({
            ...prev,
            [key]: value
        }));
        setHasChanges(true);
        onPreferencesChanged?.(true);
    }, [onPreferencesChanged]);

    const savePreferences = useCallback(async () => {
        if (!user?.uid) {
            console.error("User is not authenticated");
            return;
        }

        const preferencesRef = doc(firestore, `users/${user.uid}/answers/preferences`);
        
        // Get existing data first to avoid overwriting other preferences
        const docSnap = await getDoc(preferencesRef);
        const existingData = docSnap.exists() ? docSnap.data() : {};
        
        await setDoc(preferencesRef, {
            ...existingData,
            planType: planTypePrefs
        }, { merge: true });
        
        setHasChanges(false);
        onPreferencesChanged?.(false);
        onPreferencesSaved?.();
    }, [user?.uid, firestore, planTypePrefs, onPreferencesSaved, onPreferencesChanged]);

    return (
        <Box sx={{ width: '100%' }}>
            <Grid 
                container 
                spacing={1} 
                sx={{ 
                    width: '100%',
                    margin: '0 auto'
                }}
            >
                <Grid item xs={12}>
                    <Typography color="primary" variant="h5" sx={{mb:2}} fontWeight={600}>What type of plan do you prefer?</Typography>
                </Grid>
                
                <Grid item xs={12}>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        width: '100%',
                        mb: 1
                    }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Not Interested
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Somewhat Interested
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Strongly Prefer
                        </Typography>
                    </Box>
                </Grid>
                
                {Object.entries(planTypePrefs).map(([key, value]) => (
                    <Grid item xs={12} key={key}>
                        <Box sx={{ px: 0 }}>
                            <Typography>
                                {key === "shortterm" ? "Short Term" : key.toUpperCase()}
                            </Typography>
                            <Slider
                                value={value}
                                onChange={(_, v) => updatePlanTypePreferences(key as keyof PlanTypePreferences, v as number)}
                                step={0.1}
                                marks={[
                                    { value: 0, label: '' },
                                    { value: 0.5, label: '' },
                                    { value: 1, label: '' }
                                ]}
                                min={0}
                                max={1}
                                valueLabelDisplay="auto"
                                valueLabelFormat={(v) => {
                                    if (v <= 0.3) return "Not Interested";
                                    if (v <= 0.6) return "Somewhat Interested";
                                    return "Strongly Prefer";
                                }}
                                sx={{
                                    '& .MuiSlider-markLabel': {
                                        fontSize: '0.75rem'
                                    },
                                    '& .MuiSlider-track': {
                                        color: '#1b4b43'
                                    },
                                    '& .MuiSlider-thumb': {
                                        color: '#1b4b43'
                                    }
                                }}
                            />
                        </Box>
                    </Grid>
                ))}
            </Grid>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={!hasChanges}
                    onClick={savePreferences}
                >
                    Save
                </Button>
            </Box>
        </Box>
    );
};

export default PlanTypePref; 